/* ===========================================================
   sync/connect.js — opening a doc's live connection

   1. make sure the server knows the doc (an offline-made doc waits)
   2. ask for a 2-minute signed pass ("ticket") with the session cookie
   3. open the WebSocket with that pass
   The pass is why the socket can't be opened from another site: a
   page elsewhere can't read our API's answers to get one.
   =========================================================== */

import { api, HttpError } from '../lib/api.js';
import { isCreated } from './docs.js';

export function connector(docId, { link, signedIn = true } = {}) {
  return async () => {
    if (signedIn && !await isCreated(docId)) throw new HttpError('Failed to fetch', 0, { offline: true });
    const ticket = await getTicket(docId, link);
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return new WebSocket(`${proto}://${window.location.host}/api/docs/${docId}/live?t=${encodeURIComponent(ticket)}`);
  };
}

const sleep = ms => new Promise(r => setTimeout(r, ms));
const refused = e => e instanceof HttpError && (e.status === 404 || e.status === 403);

/* A refused pass after a long time away was nearly always a hiccup, not lost
   access: the doc said "No access" until you went back and opened it again.
   So before giving up: ask once more, then check we're still signed in and
   that the doc itself still opens. Only a doc that's really gone (or really
   not shared) stays an error; a hiccup just tries again. */
async function getTicket(docId, link) {
  const ask = () => api(`/api/docs/${docId}/ticket`, { method: 'POST', body: link ? { link } : {} }).then(r => r.ticket);
  try { return await ask(); } catch (err) {
    if (!refused(err) || err.data?.gate) throw err;
  }
  await sleep(1500);
  try { return await ask(); } catch (err) {
    if (!refused(err) || err.data?.gate || link) throw err;
    const me = await api('/api/me').catch(e => e);
    if (me && !(me instanceof Error) && !me.user) throw new HttpError('You were signed out. Sign in again and you\'ll be right back here.', 401, { signedOut: true });
    const doc = await api(`/api/docs/${docId}`).catch(e => e);
    if (refused(doc)) throw err;
    throw new HttpError('The live connection hiccuped. Trying again…', 503);
  }
}
